
import React, { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import { Lock, Mail, ArrowLeft, ShieldCheck, Eye, EyeOff, Loader, RefreshCw, Zap, Layers } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const Login: React.FC = () => {
  const { login, currentUser, setWelcomeUser, systemSettings } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secureCode, setSecureCode] = useState('');
  const [userInputCode, setUserInputCode] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginStep, setLoginStep] = useState<'idle' | 'authenticating' | 'finalizing'>('idle');
  const [sessionMessage, setSessionMessage] = useState('');

  const [isActivating, setIsActivating] = useState(false);

  useEffect(() => {
    const checkBackend = async () => {
      try {
        const res = await fetch('/api/health');
        const text = await res.text();
        if (text.includes('<title>Starting Server...</title>') || text.includes('Rate exceeded')) {
          setIsActivating(true);
        } else {
          setIsActivating(false);
          const data = JSON.parse(text);
          if (data.database && data.database.includes('disconnected')) {
             setSessionMessage(`Connection Restricted: ${data.tip || 'Database is unreachable.'}`);
          }
        }
      } catch (e) {
        // Silent
      }
    };
    checkBackend();
    const interval = setInterval(checkBackend, 10000);
    return () => clearInterval(interval);
  }, []);

  const generateCode = () => {
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setSecureCode(code);
    setUserInputCode('');
  };

  useEffect(() => {
    generateCode();
    
    const reason = sessionStorage.getItem('logout_reason');
    if (reason === 'idle') {
      setSessionMessage('تم تسجيل الخروج تلقائياً بسبب الخمول للحفاظ على أمان بياناتك.');
      sessionStorage.removeItem('logout_reason');
    }
  }, []);

  useEffect(() => {
    if (currentUser && isLoading) {
      setIsLoading(false);
      setLoginStep('idle');
    }
  }, [currentUser, isLoading]);

  useEffect(() => {
    let timer: any;
    if (isLoading) {
      timer = setTimeout(() => {
        if (isLoading && !currentUser) {
          setIsLoading(false);
          setLoginStep('idle');
          setError('يبدو أن الاتصال بالخادم بطيء جداً. يرجى التأكد من استقرار الإنترنت أو المحاولة لاحقاً.');
        }
      }, 25000); // 25s to cover all internal timeouts
    }
    return () => clearTimeout(timer);
  }, [isLoading, currentUser]);

  const handleClearSession = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/?refresh=' + Date.now();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('يرجى إدخال البريد الإلكتروني وكلمة المرور');
      return;
    }

    if (userInputCode !== secureCode) {
      setError('الرمز الأمني غير صحيح');
      generateCode();
      return;
    }

    setIsLoading(true);
    setLoginStep('authenticating');
    setError('');

    try {
      const result = await login(email, password, false, false);
      
      if (result.success) {
        setIsLoading(false);
        setLoginStep('idle');
        const welcomeName = result.user?.name || email.split('@')[0];
        setWelcomeUser({ name: welcomeName });
      } else {
        // Here we show the error from the backend if available
        setError(result.error || 'خطأ في بيانات الدخول. تأكد من البريد وكلمة المرور.');
        setIsLoading(false);
        setLoginStep('idle');
      }
    } catch (err: any) {
      console.error("[Login] Submit failed:", err);
      setError(`خطأ تقني: ${err?.message || 'فشل الاتصال بالخادم. يرجى المحاولة مرة أخرى.'}`);
      setIsLoading(false);
      setLoginStep('idle');
    }
  };

  return (
    <div className="min-h-screen bg-[#E4EEF7] flex items-center justify-center p-4 sm:p-6 overflow-hidden relative font-sans" dir="rtl">
      
      {/* Large Watermark Text */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center w-full pointer-events-none z-0">
         <div className="text-[14vw] font-black text-white/40 tracking-tighter mix-blend-overlay font-display leading-[0.8] whitespace-nowrap opacity-60">
            ALKHAIR
         </div>
      </div>

      {/* Decorative Glass Pane (Left) */}
      <div className="absolute left-[3%] top-[10%] bottom-[10%] w-[35vw] max-w-[500px] bg-white/10 backdrop-blur-xl border border-white/30 rounded-[3rem] shadow-[0_30px_100px_-20px_rgba(0,0,0,0.1)] z-0 hidden lg:block overflow-hidden mix-blend-luminosity">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-300/40 blur-[80px] rounded-full"></div>
          <div className="absolute bottom-20 -right-20 w-80 h-80 bg-indigo-300/40 blur-[100px] rounded-full"></div>
      </div>
      
      <div className="absolute left-[8%] bottom-[15%] w-48 h-64 bg-white/10 backdrop-blur-2xl border border-white/40 rounded-[2rem] shadow-2xl z-20 hidden lg:block"></div>

      {/* The Central Image */}
      <div 
         className={`absolute w-[110vw] lg:w-[75vw] h-[120vh] pointer-events-none z-10 flex items-center justify-center ${!systemSettings?.login_image_url ? 'mix-blend-darken' : ''}`}
         style={{
           left: `calc(5vw + ${systemSettings?.login_image_x || 0}vw)`,
           top: `calc(50% + ${systemSettings?.login_image_y || 0}vh)`,
           transform: 'translateY(-50%)'
         }}
      >
         <img 
           src={systemSettings?.login_image_url || "https://images.unsplash.com/photo-1552728089-571ebf4eb85c?q=80&w=1500&auto=format&fit=crop"} 
           alt="Login Design Asset" 
           className={`w-full h-full object-contain drop-shadow-2xl origin-center ${!systemSettings?.login_image_url ? 'brightness-110 contrast-125' : ''}`}
           style={{ transform: `scale(${(systemSettings?.login_image_scale || 150) / 100})` }}
         />
      </div>

      {/* Main Container */}
      <div className="w-full max-w-[1400px] min-h-[80vh] grid grid-cols-1 lg:grid-cols-2 gap-8 z-30 relative items-center px-4 md:px-10">
          
          {/* Right Side: Typography & Form (First element in RTL grid) */}
          <div className="w-full h-full flex flex-col justify-center items-start lg:pr-10 lg:pl-0">
             <div className="w-full max-w-[440px]">
               
                {/* Typography */}
                <motion.div 
                   initial={{ opacity: 0, x: 50 }}
                   animate={{ opacity: 1, x: 0 }}
                   transition={{ delay: 0.1 }}
                   className="mb-8 md:mb-12 w-full"
                >
                   <div dir="ltr" className="text-right w-full block">
                      <h1 className="text-[5rem] md:text-[7.5rem] font-black text-[#2A4259] tracking-tighter leading-[0.78] font-display uppercase">
                         RAED
                      </h1>
                      <h1 className="text-[3.5rem] md:text-[5.5rem] font-black text-[#1A6ED8] tracking-tight leading-[0.9] font-display uppercase pb-2">
                         AL KHAIR
                      </h1>
                   </div>
                   <p className="text-slate-500 mt-6 text-sm md:text-lg font-bold leading-relaxed text-right md:pr-1 opacity-80 border-r-4 border-blue-600/30 pr-6 mr-1">
                      Professional Grade Logistics Infrastructure. <br />
                      <span className="text-xs text-slate-400 mt-1 block">V4.3.0 CORE ACCESS</span>
                   </p>
                </motion.div>

               {sessionMessage && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-8 p-4 bg-amber-50/80 backdrop-blur-sm border border-amber-200 text-amber-800 rounded-2xl flex items-start gap-4 shadow-xl shadow-amber-900/5"
                  >
                    <ShieldCheck className="text-amber-500 shrink-0 mt-0.5" size={20} />
                    <span className="text-sm font-bold leading-relaxed">{sessionMessage}</span>
                  </motion.div>
                )}

               {/* Glassmorphism Form */}
               <motion.div 
                 initial={{ opacity: 0, y: 30 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.2 }}
                 className="w-full bg-white/30 backdrop-blur-3xl border border-white/60 rounded-[2rem] p-8 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] relative overflow-hidden"
               >
                  <form onSubmit={handleSubmit} className="space-y-4">
                      
                      {/* Email Field */}
                      <div className="relative group">
                          <label className="sr-only">البريد الإلكتروني</label>
                          <div className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-500 group-focus-within:text-blue-600 transition-colors pointer-events-none">
                             <Mail size={18} strokeWidth={2} />
                          </div>
                          <input 
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full h-14 bg-white/40 border border-white/50 rounded-2xl pr-12 pl-5 text-slate-800 focus:bg-white/70 focus:border-blue-400 focus:ring-4 focus:ring-blue-400/10 outline-none transition-all placeholder:text-slate-500 font-bold text-sm"
                            placeholder="البريد الإلكتروني"
                            dir="rtl"
                          />
                      </div>

                      {/* Password Field */}
                      <div className="relative group">
                          <label className="sr-only">كلمة المرور</label>
                          <div className="absolute top-1/2 -translate-y-1/2 right-4 text-slate-500 group-focus-within:text-blue-600 transition-colors pointer-events-none">
                             <Lock size={18} strokeWidth={2} />
                          </div>
                          <input 
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full h-14 bg-white/40 border border-white/50 rounded-2xl pr-12 pl-12 text-slate-800 focus:bg-white/70 focus:border-blue-400 focus:ring-4 focus:ring-blue-400/10 outline-none transition-all placeholder:text-slate-500 font-bold tracking-widest text-left text-sm"
                            placeholder="كلمة المرور"
                            dir="ltr"
                          />
                          <button 
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute top-1/2 -translate-y-1/2 left-4 text-slate-500 hover:text-slate-800 transition-colors"
                          >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                      </div>

                      {/* Verification Code Field */}
                      <div className="relative flex items-center h-14 w-full bg-white/40 border border-white/50 rounded-2xl px-2 group focus-within:bg-white/70 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-400/10 transition-all">
                          <div className="text-slate-500 mr-2 flex-shrink-0 group-focus-within:text-blue-600 transition-colors pointer-events-none">
                             <ShieldCheck size={18} strokeWidth={2} />
                          </div>
                          
                          <input 
                            type="text"
                            value={userInputCode}
                            onChange={(e) => setUserInputCode(e.target.value)}
                            className="flex-1 h-full bg-transparent outline-none text-center font-black tracking-[0.3em] text-lg text-slate-800 placeholder:text-slate-400 placeholder:font-medium placeholder:tracking-normal w-full"
                            placeholder="- - - -"
                            maxLength={4}
                            dir="ltr"
                          />
                          
                          <div className="flex items-center gap-3 bg-white/80 rounded-[0.8rem] px-3 py-1.5 shadow-sm ml-1 flex-shrink-0 border border-white/60">
                              <span className="font-space font-black text-slate-800 text-base tracking-widest select-none">{secureCode}</span>
                              <button
                                 type="button" 
                                 onClick={generateCode}
                                 className="text-blue-600 hover:bg-blue-100 p-1 rounded-md transition-colors active:scale-95 flex-shrink-0"
                              >
                                  <RefreshCw size={14} strokeWidth={3} />
                              </button>
                          </div>
                      </div>

                      <AnimatePresence>
                        {error && (
                          <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-red-500/10 border border-red-500/20 p-3 rounded-2xl flex flex-col gap-2 overflow-hidden mt-2"
                          >
                             <div className="flex items-center gap-2">
                               <Zap size={16} className="text-red-500 shrink-0" />
                               <span className="text-red-600 text-xs font-bold leading-tight">{error}</span>
                             </div>
                             {error.includes('الاتصال') && (
                               <button 
                                 type="button"
                                 onClick={handleClearSession}
                                 className="text-[10px] text-blue-600 font-black hover:underline self-start bg-blue-50 px-2 py-0.5 rounded-full"
                               >
                                 إعادة تهيئة النظام (حل بديل)
                               </button>
                             )}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="mt-6 space-y-3">
                        {isActivating && (
                          <div className="bg-blue-600 text-white p-4 rounded-2xl flex items-center gap-3 animate-pulse">
                            <Loader className="animate-spin" size={18} />
                            <span className="text-xs font-black uppercase tracking-widest">System Activating... Please Wait</span>
                          </div>
                        )}
                        <button 
                            type="submit"
                            disabled={isLoading || isActivating}
                            className="w-full h-15 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 transition-all disabled:opacity-50 disabled:cursor-wait shadow-xl active:scale-95 group"
                        >
                            {isLoading ? (
                                <>
                                  <Loader className="animate-spin" size={20} />
                                  <span>جاري التحقق...</span>
                                </>
                            ) : (
                                <>
                                    <span>تسجيل الدخول</span>
                                    <ArrowLeft size={18} strokeWidth={2.5} className="group-hover:-translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                      </div>
                  </form>
               </motion.div>
             </div>
          </div>

          <style>{`
            @keyframes scan {
              0% { top: 20%; opacity: 0; }
              50% { opacity: 1; }
              100% { top: 80%; opacity: 0; }
            }
          `}</style>

          {/* Left Side: Glassmorphism Info Cards over the Bird */}
          <div className="hidden lg:flex w-full h-full relative pointer-events-none z-10 items-center justify-center">
               
               {/* Card 1 */}
               <motion.div 
                  initial={{ opacity: 0, x: -50, y: 20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ delay: 0.3, type: "tween", duration: 0.3, ease: "easeOut" }}
                  className="absolute top-[35%] left-[20%] bg-white/30 backdrop-blur-xl border border-white/50 rounded-[1.5rem] p-4 w-64 shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)]"
               >
                   <div className="flex items-center gap-3">
                       <div className="w-10 h-10 bg-white/40 rounded-xl flex items-center justify-center text-[#2A4259] backdrop-blur-md shadow-inner overflow-hidden flex-shrink-0">
                          <Layers size={20} strokeWidth={2.5} />
                       </div>
                       <p className="text-[#2A4259] font-black text-xs leading-relaxed">
                          إدارة لوجستية متكاملة وسلسة للعمليات.
                       </p>
                   </div>
               </motion.div>

               {/* Card 2 */}
               <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, type: "tween", duration: 0.3, ease: "easeOut" }}
                  className="absolute top-[55%] left-[-5%] bg-white/30 backdrop-blur-xl border border-white/50 rounded-[1.5rem] p-4 w-64 shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)]"
               >
                   <div className="flex items-center gap-3">
                       <div className="w-10 h-10 bg-white/40 rounded-xl flex items-center justify-center text-[#2A4259] backdrop-blur-md shadow-inner overflow-hidden flex-shrink-0">
                          <ShieldCheck size={20} strokeWidth={2.5} />
                       </div>
                       <p className="text-[#2A4259] font-black text-xs leading-relaxed">
                          حماية متقدمة وموثوقية عالية للبيانات.
                       </p>
                   </div>
               </motion.div>

               {/* Card 3 */}
               <motion.div 
                  initial={{ opacity: 0, x: -50, y: -20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ delay: 0.5, type: "tween", duration: 0.3, ease: "easeOut" }}
                  className="absolute bottom-[20%] left-[25%] bg-white/30 backdrop-blur-xl border border-white/50 rounded-[1.5rem] p-4 w-64 shadow-[0_20px_40px_-5px_rgba(0,0,0,0.1)]"
               >
                   <div className="flex items-center gap-3">
                       <div className="w-10 h-10 bg-white/40 rounded-xl flex items-center justify-center text-[#1A6ED8] backdrop-blur-md shadow-inner overflow-hidden flex-shrink-0">
                          <Zap size={20} strokeWidth={2.5} />
                       </div>
                       <p className="text-[#2A4259] font-black text-xs leading-relaxed">
                          أداء استثنائي وسرعة استجابة لحظية.
                       </p>
                   </div>
               </motion.div>

          </div>

      </div>
    </div>
  );
};

export default Login;
